import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { AlertTriangle, CheckCircle, Clock, Timer } from "lucide-react";
import { useFailuresDashboard } from "../../../hooks";

const FailureDashboard = ({ failures = [] }) => {
  const remoteUtilityId = getRemoteUtilityId();
  const { data: dashboardData } = useFailuresDashboard({
    remote_utility_id: remoteUtilityId,
  });

  const metrics = {
    totalOpen: dashboardData?.totalOpenFailures || 0,
    criticalToday: dashboardData?.criticalFailures_24h || 0,
    awaitingAction: dashboardData?.awaitingAction || 0,
    avgResolutionTime: dashboardData?.avgResolutionTime || 0,
  };

  const metricsData = [
    {
      title: "Total Open Failures",
      count: metrics.totalOpen,
      trendSummary: "Currently unresolved",
      tooltip: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Definition:</strong> Total number of asset failures
            currently unresolved and requiring attention
          </li>
          <li>
            <strong>Calculation:</strong> Count of all failures with status
            "New", "Pending Action", "Service Order Created", "Assigned", or "In
            Progress"
          </li>
          <li>
            <strong>Excludes:</strong> Ignored, Resolved, and Closed failures
          </li>
          <li>
            <strong>Updates:</strong> Real-time as failures are reported or
            status changes
          </li>
        </ul>
      ),
      icon: <AlertTriangle className="h-8 w-8 text-red-500" />,
    },
    {
      title: "Critical Failures (24h)",
      count: metrics.criticalToday,
      trendSummary: "Reported today",
      tooltip: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Definition:</strong> Number of failures with "Critical"
            priority reported within the last 24 hours
          </li>
          <li>
            <strong>Calculation:</strong> Count of failures where Priority =
            "Critical" and Date/Time ≥ (Current Time - 24 hours)
          </li>
          <li>
            <strong>Includes:</strong> All status types except Closed
          </li>
          <li>
            <strong>Updates:</strong> Refreshed hourly
          </li>
        </ul>
      ),

      icon: <Clock className="h-8 w-8 text-amber-500" />,
    },
    {
      title: "Awaiting Action",
      count: metrics.awaitingAction,
      trendSummary: "Need attention",
      tooltip: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Definition:</strong> Number of failures requiring immediate
            attention or next steps
          </li>
          <li>
            <strong>Calculation:</strong> Count of failures with status "New" or
            "Pending Action"
          </li>
          <li>
            <strong>Excludes:</strong> Ignored failures and those with service
            orders already created
          </li>
          <li>
            <strong>Updates:</strong> Real-time as status changes occur
          </li>
        </ul>
      ),

      icon: <Timer className="h-8 w-8 text-orange-500" />,
    },
    {
      title: "Avg Resolution Time",
      count: metrics.avgResolutionTime,
      trendSummary: "Last 30 days",
      tooltip: (
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Definition:</strong> Average time from failure report to
            resolution over the last 30 days
          </li>
          <li>
            <strong>Calculation:</strong> Sum of (Resolution Date - Report Date)
            ÷ Count of resolved failures
          </li>
          <li>
            <strong>Excludes:</strong> Open/unresolved failures and ignored
            failures from calculation
          </li>
        </ul>
      ),

      icon: <CheckCircle className="h-8 w-8 text-green-500" />,
      suffix: " hrs",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-2">
      {metricsData.map((stat, index) => (
        <QuickStatsCard
          key={index}
          title={stat.title}
          count={stat.count}
          trendSummary={stat.trendSummary}
          icon={stat.icon}
          tooltip={stat.tooltip}
        />
      ))}
    </div>
  );
};

export default FailureDashboard;
